import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { recoveryApi } from './api.ts';
import {
  eligibleRecoveryUsers,
  thresholdOptions,
  validateCircleSetup,
} from './recoveryUi.ts';
import type {
  CreateCircleInput,
  RecoveryUser,
  RecoveryUserId,
} from './types.ts';

const UNAVAILABLE_INSTITUTIONS = [
  {
    name: 'Havn Sparebank',
    description: 'Proposed institutional custody for future integrations.',
  },
  {
    name: 'Stiftelsen Åpen Dør',
    description: 'Proposed crisis and digital-rights custody for future integrations.',
  },
] as const;

const loadDefaultUsers = () => recoveryApi.getUsers();

export interface OwnerCircleSetupProps {
  users?: RecoveryUser[];
  currentUserId: RecoveryUserId;
  loadUsers?: () => Promise<RecoveryUser[]>;
  onCreate: (input: CreateCircleInput) => Promise<void>;
}

export default function OwnerCircleSetup({
  users,
  currentUserId,
  loadUsers = loadDefaultUsers,
  onCreate,
}: OwnerCircleSetupProps) {
  const [loadedUsers, setLoadedUsers] = useState<RecoveryUser[]>(users || []);
  const [loadingUsers, setLoadingUsers] = useState(users === undefined);
  const [selectedIds, setSelectedIds] = useState<RecoveryUserId[]>([]);
  const [threshold, setThreshold] = useState(2);
  const [revealMemberNames, setRevealMemberNames] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (users !== undefined) {
      setLoadedUsers(users);
      setLoadingUsers(false);
      return;
    }

    let active = true;
    setLoadingUsers(true);
    loadUsers()
      .then((contacts) => {
        if (active) {
          setLoadedUsers(contacts);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(
            cause instanceof Error
              ? cause.message
              : 'Could not load personal contacts.'
          );
        }
      })
      .finally(() => {
        if (active) setLoadingUsers(false);
      });
    return () => {
      active = false;
    };
  }, [loadUsers, users]);

  const contacts = useMemo(
    () => eligibleRecoveryUsers(loadedUsers, currentUserId),
    [currentUserId, loadedUsers]
  );
  const selectedKey = useMemo(
    () => new Set(selectedIds.map(String)),
    [selectedIds]
  );
  const availableThresholds = thresholdOptions(selectedIds.length);

  useEffect(() => {
    if (selectedIds.length >= 2 && threshold > selectedIds.length) {
      setThreshold(selectedIds.length);
    }
  }, [selectedIds.length, threshold]);

  const toggleUser = (user: RecoveryUser, checked: boolean) => {
    setError(null);
    setSelectedIds((current) => {
      if (!checked) {
        return current.filter((id) => String(id) !== String(user.id));
      }
      if (
        current.some((id) => String(id) === String(user.id)) ||
        current.length >= 8
      ) {
        return current;
      }
      return [...current, user.id];
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationErrors = validateCircleSetup(
      selectedIds,
      currentUserId,
      threshold
    );
    if (validationErrors.length) {
      setError(validationErrors.join(' '));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onCreate({
        threshold,
        revealMemberNames,
        members: selectedIds.map((holderUserId) => ({
          holderType: 'user',
          holderUserId,
        })),
      });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not create the circle.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Alert variant="info">
        <strong>A keyholder has one narrow role:</strong> hold encrypted recovery
        custody and decide on a named request. A keyholder cannot read your
        messages, act as a guardian, or start an incapacity recovery.
      </Alert>

      <section aria-labelledby="recovery-people-heading">
        <h3 id="recovery-people-heading" className="h5">
          1. Choose 2–8 personal keyholders
        </h3>
        <p className="text-body-secondary">
          Every person must accept. You cannot select yourself or the same
          person twice.
        </p>

        {loadingUsers ? (
          <p role="status">
            <Spinner size="sm" className="me-2" aria-hidden="true" />
            Loading contacts…
          </p>
        ) : contacts.length ? (
          <fieldset className="recovery-choice-list mb-4">
            <legend className="visually-hidden">Personal keyholders</legend>
            {contacts.map((user, index) => {
              const inputId = `recovery-holder-${index}`;
              const checked = selectedKey.has(String(user.id));
              return (
                <Form.Check
                  key={String(user.id)}
                  id={inputId}
                  type="checkbox"
                  checked={checked}
                  disabled={!checked && selectedIds.length >= 8}
                  onChange={(event) => toggleUser(user, event.target.checked)}
                  label={
                    <span>
                      {user.emoji ? `${user.emoji} ` : ''}
                      <strong>{user.fullName}</strong>
                      {user.shortName && user.shortName !== user.fullName
                        ? ` (${user.shortName})`
                        : ''}
                    </span>
                  }
                />
              );
            })}
          </fieldset>
        ) : (
          <Alert variant="secondary">
            No eligible personal contacts are available. Add or activate
            contacts before creating a circle.
          </Alert>
        )}
      </section>

      <section aria-labelledby="recovery-institutions-heading">
        <h3 id="recovery-institutions-heading" className="h5">
          Institution options
        </h3>
        <Row className="g-3 mb-3">
          {UNAVAILABLE_INSTITUTIONS.map((institution) => (
            <Col md={6} key={institution.name}>
              <Card className="h-100 recovery-unavailable-card" aria-disabled="true">
                <Card.Body>
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <Card.Title as="h4" className="h6">
                      {institution.name}
                    </Card.Title>
                    <Badge bg="secondary">Integration not available</Badge>
                  </div>
                  <Card.Text>{institution.description}</Card.Text>
                  <p className="small mb-0">
                    It cannot be selected and holds nothing in this first slice.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        <p className="small text-body-secondary">
          There is no government keyholder. Court or public-authority access is
          a separate legal process and never begins as a recovery-circle contact.
        </p>
      </section>

      <section className="mt-4" aria-labelledby="recovery-threshold-heading">
        <h3 id="recovery-threshold-heading" className="h5">
          2. Choose how many must approve
        </h3>
        <Row>
          <Col sm={8} md={5}>
            <Form.Group controlId="recovery-threshold">
              <Form.Label>Approval threshold</Form.Label>
              <Form.Select
                value={threshold}
                disabled={selectedIds.length < 2}
                onChange={(event) => setThreshold(Number(event.target.value))}
              >
                {availableThresholds.length ? (
                  availableThresholds.map((value) => (
                    <option value={value} key={value}>
                      {value} of {selectedIds.length}
                    </option>
                  ))
                ) : (
                  <option value={2}>Choose at least two people first</option>
                )}
              </Form.Select>
              <Form.Text>
                One-out-of-one is forbidden. Requiring everyone is allowed, but
                recovery fails if one person is unavailable.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </section>

      <section className="mt-4" aria-labelledby="recovery-privacy-heading">
        <h3 id="recovery-privacy-heading" className="h5">
          3. Choose roster privacy
        </h3>
        <Form.Check
          id="recovery-reveal-names"
          type="switch"
          checked={revealMemberNames}
          onChange={(event) => setRevealMemberNames(event.target.checked)}
          label="Let keyholders see every member’s name"
        />
        <p className="small text-body-secondary mt-2">
          Keyholders always see the circle size and threshold. With this off,
          names stay hidden, but they are still told whether an institution is
          present.
        </p>
      </section>

      <div aria-live="polite" aria-atomic="true">
        {error && <Alert variant="danger">{error}</Alert>}
      </div>
      <Button type="submit" disabled={busy || selectedIds.length < 2}>
        {busy ? 'Creating invitations…' : 'Create circle and invite keyholders'}
      </Button>
    </Form>
  );
}
